/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { createPortal } from 'react-dom';

import { getDocument } from '@atlaskit/browser-apis';
import { token } from '@atlaskit/tokens';

// Fraction of scrollable distance (`scrollHeight - clientHeight`) to scroll
// past before the container fades in. Distance-based so it scales with results.
const DEFAULT_SCROLL_VISIBILITY_THRESHOLD = 0.1;

// `data-testid` of the FPS scroll container (the `<Body />` in
// `common/ui/layout-body`). More reliable than "first scrollable ancestor".
const FPS_SCROLL_CONTAINER_TESTID = 'search-page-body';

// Extra breathing room (px) added on top of the measured floating-box height
// when reserving scroll space, so the last result clears the box rather than
// sitting flush against it. Matches the box's `bottom` gap visually.
const RESERVED_SPACE_GUTTER = 24;

const floatingContainerStyles = css({
	position: 'fixed',
	left: '50%',
	bottom: token('space.250'),
	width: `min(720px, calc(100% - ${token('space.400')}))`,
	// Above standard FPS content but below modal/dialog layers.
	zIndex: 50,
	// Hidden state: faded out, nudged down for the slide-up reveal, and
	// non-interactive. `pointer-events: none` stops it intercepting clicks;
	// focus is additionally blocked via the `inert` attribute (see render).
	opacity: 0,
	pointerEvents: 'none',
	// `translateX(-50%)` centres horizontally; `translateY(8px)` is the hidden
	// offset that animates back to 0 on reveal (the `transform` transition leg).
	transform: 'translateX(-50%) translateY(8px)',
	transition: 'opacity 160ms ease-out, transform 160ms ease-out',
	borderRadius: token('space.150'),
	boxShadow: token('elevation.shadow.overlay'),
});

const floatingContainerVisibleStyles = css({
	opacity: 1,
	pointerEvents: 'auto',
	transform: 'translateX(-50%) translateY(0)',
});

// Applied when matching a target's width: `left`/`width` are written imperatively
// via a ref (see the width effect), so these styles only need to drop the
// `translateX(-50%)` centring leg from the reveal transform. Keeping visibility
// class-driven means width updates never trigger a React re-render.
const floatingContainerMatchedStyles = css({
	transform: 'translateY(8px)',
});

const floatingContainerMatchedVisibleStyles = css({
	transform: 'translateY(0)',
});

const anchorStyles = css({
	// Invisible, zero-size anchor with no layout impact.
	width: 0,
	height: 0,
	overflow: 'hidden',
});

// Walks up from `el` to find the FPS scroll container by `data-testid`, falling
// back to a document-wide lookup. Returns `null` if not found (the caller then
// renders nothing further — there is no `window` scroll fallback).
const findFpsScrollContainer = (el: Element | null): Element | null => {
	const doc = getDocument();
	if (!doc) {
		return null;
	}
	// Walk up the ancestor chain first to avoid matching unrelated FPS bodies.
	let node: Element | null = el?.parentElement ?? null;
	while (node && node !== doc.documentElement) {
		if (node.getAttribute('data-testid') === FPS_SCROLL_CONTAINER_TESTID) {
			return node;
		}
		node = node.parentElement;
	}
	// Fallback: document-wide search (useful in tests / portaled mounts).
	return doc.querySelector(`[data-testid="${FPS_SCROLL_CONTAINER_TESTID}"]`);
};

export type FpsFloatingContainerProps = {
	/**
	 * Content rendered inside the floating container.
	 */
	children: ReactNode;
	/**
	 * Fraction (0–1) of the FPS scroll container's scrollable distance the user
	 * must scroll past before the container fades in. Defaults to `0.1`.
	 */
	threshold?: number;
	/**
	 * `data-testid` applied to the floating container element.
	 */
	testId?: string;
	/**
	 * When set, the container matches the `left`/`width` of the element with this
	 * `data-testid` (e.g. the FPS results column) instead of being centred. It
	 * re-measures on target resize and page scroll/resize. Falls back to centred
	 * sizing if the target isn't found.
	 */
	matchWidthTestId?: string;
};

/**
 * FPS floating container: a `position: fixed` box (portaled into `document.body`
 * to avoid containing-block clipping) that fades in once the user scrolls past
 * {@link FpsFloatingContainerProps.threshold} of the FPS `<Body />`'s scrollable
 * distance, and fades out above it. The FPS `<Body />` is the scroll surface; if
 * it isn't present at mount, the bar simply never shows (no fallback).
 */
export const FpsFloatingContainer = ({
	children,
	threshold = DEFAULT_SCROLL_VISIBILITY_THRESHOLD,
	testId,
	matchWidthTestId,
}: FpsFloatingContainerProps): React.JSX.Element | null => {
	const [isVisible, setIsVisible] = useState<boolean>(false);
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const floatingRef = useRef<HTMLDivElement | null>(null);
	const reservedPaddingRef = useRef<number>(0);

	useEffect(() => {
		const anchor = anchorRef.current;
		if (!anchor) {
			return;
		}

		// The FPS body is the scroll surface. `scroll` events don't bubble, so we
		// bind directly to it. If it isn't present, there's nothing meaningful to
		// observe (this component only makes sense inside FPS results), so we do
		// nothing and the bar simply never shows — no `window` fallback.
		const scrollContainer = findFpsScrollContainer(anchor);
		if (!scrollContainer) {
			return;
		}

		const evaluate = () => {
			const scrollTop = scrollContainer.scrollTop;
			// Exclude any bottom space we've reserved for the floating box so the
			// reveal threshold tracks the natural content height (prevents flicker
			// at the boundary, where adding padding would otherwise re-hide the box).
			const scrollableDistance = Math.max(
				0,
				scrollContainer.scrollHeight - scrollContainer.clientHeight - reservedPaddingRef.current,
			);

			if (scrollableDistance === 0) {
				// Nothing to scroll (e.g. very few results) — never reveal,
				// otherwise it would appear on initial render.
				setIsVisible(false);
				return;
			}

			const next = scrollTop >= scrollableDistance * threshold;
			setIsVisible((prev) => (prev === next ? prev : next));
		};

		const unbindScroll = bind(scrollContainer, {
			type: 'scroll',
			listener: evaluate,
			options: { passive: true },
		});
		const unbindResize = bind(window, {
			type: 'resize',
			listener: evaluate,
			options: { passive: true },
		});
		// Initial evaluation — covers restored scroll on back-nav, etc.
		evaluate();

		return () => {
			unbindScroll();
			unbindResize();
		};
	}, [threshold]);

	// While visible, align the box to the results column: sync its `left`/`width`
	// (written imperatively, only on change), and reserve bottom space on the
	// column so the last result can scroll clear of the fixed box.
	useEffect(() => {
		if (!matchWidthTestId || !isVisible) {
			return;
		}

		const doc = getDocument();
		const target = doc?.querySelector(`[data-testid="${matchWidthTestId}"]`);
		if (!(target instanceof HTMLElement)) {
			return;
		}

		// Preserve whatever inline `padding-bottom` was set before we touched it so
		// we can restore it exactly (covers the empty-string "no inline" case too).
		const originalPaddingBottom = target.style.paddingBottom;
		const box = floatingRef.current;
		if (box) {
			// Add our space on top of the column's existing (computed) padding so we
			// don't clobber its bottom gap. Only `extra` is our contribution, so
			// that's what the flicker guard excludes — `existing` is already in
			// `scrollHeight`.
			const existing =
				parseFloat(doc?.defaultView?.getComputedStyle(target).paddingBottom ?? '0') || 0;
			const extra = box.offsetHeight + RESERVED_SPACE_GUTTER;
			reservedPaddingRef.current = extra;
			target.style.paddingBottom = `${existing + extra}px`;
		}

		let lastLeft = NaN;
		let lastWidth = NaN;

		// Measure the target and write the box's `left`/`width` if they changed.
		const syncPosition = () => {
			const el = floatingRef.current;
			if (!el) {
				return;
			}
			const rect = target.getBoundingClientRect();
			if (rect.left !== lastLeft || rect.width !== lastWidth) {
				lastLeft = rect.left;
				lastWidth = rect.width;
				el.style.left = `${rect.left}px`;
				el.style.width = `${rect.width}px`;
			}
		};

		// Coalesce bursts of scroll/resize events into a single measure per frame,
		// so the layout read happens before paint rather than synchronously inside
		// each event, and never more than once per frame.
		let rafId = 0;
		const scheduleSync = () => {
			if (rafId) {
				return;
			}
			rafId = requestAnimationFrame(() => {
				rafId = 0;
				syncPosition();
			});
		};

		// Measure synchronously on reveal before paint. The matched styles drop the
		// `translateX(-50%)` centring leg, so without this the box would paint at
		// `left: 50%` (uncentred) for one frame before the first correction — a
		// visible left-shift flash on reveal.
		syncPosition();

		// Re-measure when the target's own box changes (column resize, async content
		// loading, font swaps) — these don't necessarily fire scroll/resize.
		const observer = new ResizeObserver(scheduleSync);
		observer.observe(target);

		// Horizontal position can also shift on scroll (e.g. sticky/overflow layout
		// changes) and on viewport resize. Reuse the FPS scroll surface the
		// visibility effect already keys off; fall back to nothing if absent.
		const scrollContainer = findFpsScrollContainer(anchorRef.current);
		const unbindScroll = scrollContainer
			? bind(scrollContainer, {
					type: 'scroll',
					listener: scheduleSync,
					options: { passive: true },
				})
			: undefined;
		const unbindResize = doc?.defaultView
			? bind(doc.defaultView, {
					type: 'resize',
					listener: scheduleSync,
					options: { passive: true },
				})
			: undefined;

		return () => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
			observer.disconnect();
			unbindScroll?.();
			unbindResize?.();
			reservedPaddingRef.current = 0;
			target.style.paddingBottom = originalPaddingBottom;
		};
	}, [matchWidthTestId, isVisible]);

	// Clear the imperative `left`/`width` overrides when width matching is off.
	useEffect(() => {
		if (matchWidthTestId) {
			return;
		}
		const el = floatingRef.current;
		if (el) {
			el.style.left = '';
			el.style.width = '';
		}
	}, [matchWidthTestId]);

	const doc = getDocument();
	if (!doc) {
		// SSR / non-browser environment — render nothing rather than throw.
		return null;
	}

	const isMatching = Boolean(matchWidthTestId);

	return (
		<React.Fragment>
			{/* Zero-size anchor used only to locate the FPS scroll container. */}
			<div ref={anchorRef} css={anchorStyles} aria-hidden="true" />
			{/* Portaled into `document.body` so `position: fixed` isn't clipped. */}
			{createPortal(
				<div
					ref={floatingRef}
					css={[
						floatingContainerStyles,
						isVisible && floatingContainerVisibleStyles,
						isMatching && floatingContainerMatchedStyles,
						isMatching && isVisible && floatingContainerMatchedVisibleStyles,
					]}
					// `inert` (rendered by default while hidden) removes the subtree
					// from the tab order and a11y tree — unlike `aria-hidden`, which
					// would leave children focusable (an `aria-hidden-focus`
					// violation). Applied at render time (not via an effect) so the
					// container is inert from the very first paint, with no gap.
					{...(isVisible ? {} : { inert: '' })}
					data-testid={testId}
				>
					{children}
				</div>,
				doc.body,
			)}
		</React.Fragment>
	);
};
